import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ChevronLeft, Clock, Tag, BookOpen,
  Loader2, AlertCircle, Flame,
} from "lucide-react";
import articlesApi from "../api/articles";
import { useAuth } from "../hooks/useAuth";
import SEOHead from "../components/SEOHead";
import StudyBar from "../components/StudyBar";

/* ── Markdown renderer ───────────────────────────────────────────────────── */
function Inline({ text }) {
  const parts = [];
  let rest = text;
  let key = 0;
  const push = (el) => parts.push(el);

  while (rest.length) {
    const bold  = rest.match(/^(.*?)\*\*([\s\S]+?)\*\*/);
    const italic = rest.match(/^(.*?)\*([\s\S]+?)\*/);
    const code  = rest.match(/^(.*?)`([^`]+)`/);

    let earliest = null;
    if (bold   && (!earliest || bold.index   < earliest.index)) earliest = { type: 'bold',   m: bold };
    if (italic && (!earliest || italic.index < earliest.index)) earliest = { type: 'italic', m: italic };
    if (code   && (!earliest || code.index   < earliest.index)) earliest = { type: 'code',   m: code };

    if (!earliest) { push(<span key={key++}>{rest}</span>); break; }
    const { type, m } = earliest;
    if (m[1]) push(<span key={key++}>{m[1]}</span>);
    if (type === 'bold')   push(<strong key={key++} className="font-bold text-gray-900">{m[2]}</strong>);
    if (type === 'italic') push(<em key={key++} className="italic">{m[2]}</em>);
    if (type === 'code')   push(<code key={key++} className="bg-indigo-50 text-indigo-700 px-1 py-0.5 rounded text-[0.85em] font-mono">{m[2]}</code>);
    rest = rest.slice(m[0].length);
  }
  return <>{parts}</>;
}

function Prose({ text }) {
  if (!text) return null;
  const lines  = text.split("\n");
  const blocks = [];
  let listBuf  = [];
  let ordBuf   = [];
  let tblBuf   = [];
  let key      = 0;

  function flushList() {
    if (!listBuf.length) return;
    blocks.push(
      <ul key={key++} className="list-disc list-outside ml-5 space-y-1.5 text-gray-700 text-sm leading-relaxed mb-4">
        {listBuf.map((t, i) => <li key={i}><Inline text={t} /></li>)}
      </ul>
    );
    listBuf = [];
  }
  function flushOrd() {
    if (!ordBuf.length) return;
    blocks.push(
      <ol key={key++} className="list-decimal list-outside ml-5 space-y-1.5 text-gray-700 text-sm leading-relaxed mb-4">
        {ordBuf.map((t, i) => <li key={i}><Inline text={t} /></li>)}
      </ol>
    );
    ordBuf = [];
  }
  function flushTable() {
    if (!tblBuf.length) return;
    const rows = tblBuf.filter(r => !/^\|[-| :]+\|$/.test(r.trim()));
    const parsed = rows.map(r => r.replace(/^\||\|$/g, '').split('|').map(c => c.trim()));
    const [head, ...body] = parsed;
    blocks.push(
      <div key={key++} className="overflow-x-auto mb-4">
        <table className="min-w-full border border-gray-200 rounded-xl overflow-hidden text-sm">
          <thead className="bg-indigo-50">
            <tr>{head.map((c, i) => <th key={i} className="px-4 py-2 text-left font-bold text-indigo-800 border-b border-gray-200">{c}</th>)}</tr>
          </thead>
          <tbody>
            {body.map((row, ri) => (
              <tr key={ri} className={ri % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                {row.map((c, ci) => <td key={ci} className="px-4 py-2 text-gray-700 border-b border-gray-100"><Inline text={c} /></td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
    tblBuf = [];
  }

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const raw  = line.trim();

    if (!raw) {
      flushList(); flushOrd(); flushTable();
      continue;
    }
    if (/^\|/.test(raw)) {
      flushList(); flushOrd();
      tblBuf.push(raw);
      continue;
    }
    flushTable();

    if (/^---+$/.test(raw)) {
      flushList(); flushOrd();
      blocks.push(<hr key={key++} className="my-6 border-gray-200" />);
      continue;
    }
    if (/^####\s/.test(raw)) {
      flushList(); flushOrd();
      blocks.push(<h4 key={key++} className="font-sora font-bold text-sm text-gray-800 mt-4 mb-1.5"><Inline text={raw.slice(5)} /></h4>);
      continue;
    }
    if (/^###\s/.test(raw)) {
      flushList(); flushOrd();
      blocks.push(<h3 key={key++} className="font-sora font-bold text-base text-gray-900 mt-5 mb-2 pb-1 border-b border-gray-100"><Inline text={raw.slice(4)} /></h3>);
      continue;
    }
    if (/^##\s/.test(raw)) {
      flushList(); flushOrd();
      blocks.push(<h2 key={key++} className="font-sora font-extrabold text-lg text-indigo-900 mt-6 mb-3"><Inline text={raw.slice(3)} /></h2>);
      continue;
    }
    if (/^#\s/.test(raw)) {
      flushList(); flushOrd();
      blocks.push(<h1 key={key++} className="font-sora font-black text-xl text-indigo-900 mt-6 mb-3"><Inline text={raw.slice(2)} /></h1>);
      continue;
    }
    if (/^>\s/.test(raw)) {
      flushList(); flushOrd();
      blocks.push(
        <blockquote key={key++} className="border-l-4 border-indigo-400 pl-4 py-1 bg-indigo-50 rounded-r-xl mb-3 text-sm text-indigo-800 italic">
          <Inline text={raw.slice(2)} />
        </blockquote>
      );
      continue;
    }
    if (/^[-*]\s/.test(raw)) {
      flushOrd(); flushTable();
      listBuf.push(raw.slice(2));
      continue;
    }
    if (/^\d+\.\s/.test(raw)) {
      flushList(); flushTable();
      ordBuf.push(raw.replace(/^\d+\.\s/, ''));
      continue;
    }

    flushList(); flushOrd(); flushTable();
    blocks.push(
      <p key={key++} className="text-sm text-gray-700 leading-relaxed mb-3">
        <Inline text={raw} />
      </p>
    );
  }
  flushList(); flushOrd(); flushTable();
  return <>{blocks}</>;
}

/* ── Category dot colour ─────────────────────────────────────────────────── */
const CAT_DOT = {
  "Economy":              "#10B981",
  "National":             "#D97706",
  "International":        "#3B82F6",
  "Defence":              "#EA580C",
  "Government Schemes":   "#2563EB",
  "Science & Technology": "#6366F1",
  "Environment":          "#22C55E",
  "Society":              "#7C3AED",
  "Analysis":             "#EC4899",
};

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
function fmtDate(dateStr) {
  if (!dateStr) return "";
  const [y, m, d] = dateStr.split("-");
  return `${parseInt(d, 10)} ${MONTHS[parseInt(m, 10) - 1]} ${y}`;
}

/* ── Main ────────────────────────────────────────────────────────────────── */
export default function ArticleDetail() {
  const { slug }   = useParams();
  const navigate   = useNavigate();
  const { user }   = useAuth();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  useEffect(() => {
    setLoading(true);
    articlesApi.getBySlug(slug)
      .then(setArticle)
      .catch(() => setError("Article not found."))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F6FA] flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <Loader2 size={28} className="animate-spin text-gray-300" />
          <p className="text-xs text-gray-400">Loading article…</p>
        </div>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="min-h-screen bg-[#F5F6FA] flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <AlertCircle size={32} className="text-red-400" />
          <p className="text-sm text-red-500">{error || "Article not found"}</p>
          <button onClick={() => navigate("/articles")} className="text-xs text-indigo-600 font-semibold hover:underline">
            ← Back to Articles
          </button>
        </div>
      </div>
    );
  }

  const dotColor = CAT_DOT[article.category] || "#9CA3AF";

  return (
    <div className="min-h-screen bg-[#F5F6FA] font-body">
      {article && (
        <SEOHead
          title={`${article.title} | EduPulseAI`}
          description={article.summary || article.title}
          keywords={`${article.category}, ${article.title}, NDA ${article.category}, CDS preparation`}
          url={`/articles/${article.slug}`}
          jsonLd={{ "@context": "https://schema.org", "@type": "Article", "headline": article.title, "description": article.summary || article.title, "datePublished": article.dateStr, "publisher": { "@type": "Organization", "name": "EduPulseAI" } }}
        />
      )}
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 h-14 flex items-center gap-3">
          <button onClick={() => navigate("/articles")}
            className="flex items-center gap-1.5 text-sm font-semibold text-gray-400 hover:text-gray-700 transition-colors flex-shrink-0">
            <ChevronLeft size={15} /> Articles
          </button>
          <span className="text-gray-200">|</span>
          <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: dotColor }} />
          <span className="text-xs font-bold text-gray-500 truncate">{article.category}</span>
        </div>
      </div>

      {/* Article */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.22 }}
        className="max-w-4xl mx-auto px-4 sm:px-6 py-6 pb-24 sm:pb-8"
      >
        {/* Meta card */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden mb-6">
          <div className="h-1.5 w-full" style={{ background: `linear-gradient(90deg, ${dotColor}, ${dotColor}88)` }} />
          <div className="p-5 sm:p-7">
            {/* Featured badge */}
            {article.featured && (
              <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-amber-50 text-amber-600 mb-3">
                <Flame size={10} /> Featured
              </span>
            )}

            <h1 className="font-sora font-black text-2xl sm:text-3xl text-gray-900 leading-tight mb-3">
              {article.title}
            </h1>

            {article.summary && (
              <p className="text-sm text-gray-600 leading-relaxed mb-4 border-l-4 border-indigo-300 pl-3 bg-indigo-50 py-2 rounded-r-xl">
                {article.summary}
              </p>
            )}

            {/* Metadata row */}
            <div className="flex flex-wrap items-center gap-3 text-[11px] text-gray-400 font-medium">
              <span>{fmtDate(article.dateStr)}</span>
              {article.readTime && (
                <span className="flex items-center gap-1"><Clock size={11} /> {article.readTime} min read</span>
              )}
            </div>

            {/* Exam tags */}
            {article.examTags?.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-3">
                {article.examTags.map(t => (
                  <span key={t} className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700">
                    {t}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 sm:p-8 mb-6">
          <Prose text={article.content} />
        </div>

        {/* Tags */}
        {article.tags?.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap">
            <Tag size={13} className="text-gray-400" />
            {article.tags.map(t => (
              <span key={t} className="text-[11px] text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full capitalize">
                {t}
              </span>
            ))}
          </div>
        )}

        {/* Back link */}
        <button
          onClick={() => navigate("/articles")}
          className="mt-6 mb-20 flex items-center gap-1.5 text-sm font-semibold text-indigo-600 hover:text-indigo-800 transition-colors"
        >
          <BookOpen size={14} /> Back to Articles
        </button>
      </motion.div>

      <StudyBar
        itemType="article"
        itemSlug={article.slug}
        itemTitle={article.title}
        isLoggedIn={!!user}
      />
    </div>
  );
}
